import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import {
  queryFilterOptions,
  type FilterDef,
  type FilterOption,
} from '@nucleus-commerce/admin-kit/index-table';
import {
  OrderApprovalStatus,
  OrderFinancialStatus,
  OrderFulfillmentStatus,
  OrderLabelInput,
  OrderStatus,
  type Customer,
  type OrderSummary,
} from '@nucleus-commerce/api-client/admin';

import { customerListQueryOptions, customerName, customerQueryOptions } from '@/features/customers';

/** Fields GET /orders sorts on; anything else is a 400. */
export const ORDER_SORTS = ['placedAt', 'total', 'name'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key. Any `currency` marks `total` as whole minor units; the page shows it in
 * the store's currency.
 */
export const ORDER_FILTER_FIELDS = [
  { key: 'status', type: 'enum', negatable: true, options: valuesOf(OrderStatus) },
  {
    key: 'financialStatus',
    type: 'enum',
    negatable: true,
    options: valuesOf(OrderFinancialStatus),
  },
  {
    key: 'fulfillmentStatus',
    type: 'enum',
    negatable: true,
    options: valuesOf(OrderFulfillmentStatus),
  },
  { key: 'placedAt', type: 'date-range' },
  { key: 'total', type: 'number-range', currency: 'USD' },
  { key: 'customerId', type: 'async' },
  { key: 'label', type: 'enum', options: valuesOf(OrderLabelInput) },
  { key: 'approvalStatus', type: 'enum', options: valuesOf(OrderApprovalStatus) },
  { key: 'tags', type: 'text' },
  { key: 'hasReturns', type: 'boolean' },
  { key: 'workflowStage', type: 'text' },
] as const;

const customerOption = (customer: Customer): FilterOption => ({
  value: customer.id,
  label: customerName(customer) ?? customer.email,
  description: customer.email,
});

export interface OrderFiltersOptions {
  /** The `orders` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
  currency: string;
}

/** Filters of the orders table, in the order the filter menu lists them. */
export function orderFilters({
  t,
  queryClient,
  storeId,
  currency,
}: OrderFiltersOptions): Array<FilterDef<OrderSummary>> {
  const options = (values: Record<string, string>, prefix: string): FilterOption[] =>
    valuesOf(values).map(({ value }) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      negatable: true,
      pinned: true,
      options: options(OrderStatus, 'status'),
    },
    {
      key: 'financialStatus',
      label: t('filters.financialStatus'),
      type: 'enum',
      negatable: true,
      pinned: true,
      aliases: ['payment'],
      options: options(OrderFinancialStatus, 'financialStatus'),
    },
    {
      key: 'fulfillmentStatus',
      label: t('filters.fulfillmentStatus'),
      type: 'enum',
      negatable: true,
      pinned: true,
      aliases: ['fulfillment'],
      options: options(OrderFulfillmentStatus, 'fulfillmentStatus'),
    },
    { key: 'placedAt', label: t('filters.placedAt'), type: 'date-range', aliases: ['placed'] },
    { key: 'total', label: t('filters.total'), type: 'number-range', currency, min: 0 },
    {
      key: 'customerId',
      label: t('filters.customer'),
      type: 'async',
      // The endpoint takes one customer.
      multiple: false,
      aliases: ['customer'],
      ...queryFilterOptions(queryClient, {
        search: (q) => customerListQueryOptions(storeId, { ...(q ? { q } : {}), limit: 20 }),
        toOptions: (page) => page.items.map(customerOption),
        resolve: {
          query: ([id]) => customerQueryOptions(storeId, id ?? ''),
          toOptions: (customer) => [customerOption(customer)],
        },
      }),
    },
    {
      key: 'approvalStatus',
      label: t('filters.approvalStatus'),
      type: 'enum',
      aliases: ['design'],
      options: options(OrderApprovalStatus, 'approvalStatus'),
    },
    {
      key: 'tags',
      label: t('filters.tags'),
      type: 'text',
      aliases: ['tag'],
      placeholder: t('filters.tagsPlaceholder'),
    },
    {
      key: 'hasReturns',
      label: t('filters.hasReturns'),
      type: 'boolean',
      aliases: ['returns'],
      trueLabel: t('filters.withReturns'),
      falseLabel: t('filters.withoutReturns'),
    },
    {
      key: 'workflowStage',
      label: t('filters.workflowStage'),
      type: 'text',
      aliases: ['stage'],
      placeholder: t('filters.workflowStagePlaceholder'),
    },
    {
      key: 'label',
      label: t('production:filters.label'),
      type: 'enum',
      // The endpoint takes one production label.
      multiple: false,
      options: Object.values(OrderLabelInput).map((value) => ({
        value,
        label: t(`production:label.${value}`),
      })),
    },
  ];
}
